import React, { useCallback, useEffect, useMemo, useState } from "react";

import { debounce } from "lodash";
import { FormProvider, useForm } from "react-hook-form";

import FormTextField from "@covertree/ui/forms/FormTextField";
import CustomIconButton from "@covertree/ui/buttons/IconButton";
import { Table } from "@covertree/ui/table/Table";
import { TextButtonWithIcon } from "@covertree/ui/buttons";

import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { Column, Row } from "../table/types";

type SearchForm = {
  query: string;
};

type Props =
  | {
      type: "DEFAULT_CONTENT";
      title: string;
      resultsTitle?: string;
      placeholder: string;
      loading: boolean;
      rows?: Row[];
      columns: Column[];
      searchFn: (queryString: string) => void;
    }
  | {
      type: "CUSTOM_CONTENT";
      title: string;
      placeholder: string;
      loading: boolean;
      resultsContent: React.ReactNode;
      searchFn: (queryString: string) => void;
    };

export const SearchBox = ({
  title,
  placeholder,
  loading,
  searchFn,
  ...props
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const form = useForm<SearchForm>({
    mode: "all",
    defaultValues: { query: "" },
  });

  const { control, setValue, watch } = form;

  const handleOpen = useCallback(() => {
    setIsTooltipOpen(false);
    setIsDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const query = watch("query");

  const search = useMemo(
    () =>
      debounce((queryString: string) => {
        searchFn(queryString);
      }, 1000),
    [searchFn]
  );

  useEffect(() => {
    if (query && query.length >= 2) {
      search(query);
    }
  }, [search, query]);

  return (
    <>
      <Tooltip open={isTooltipOpen} placement="bottom" title={title}>
        <TextButtonWithIcon
          onMouseEnter={() => setIsTooltipOpen(true)}
          onMouseLeave={() => setIsTooltipOpen(false)}
          aria-label="Search"
          onClick={handleOpen}
          data-testid="search-button"
          icon={<Search />}>
          Search
        </TextButtonWithIcon>
      </Tooltip>
      <Dialog
        open={isDialogOpen}
        fullWidth
        maxWidth="lg"
        onClose={handleClose}
        sx={{
          zIndex: 1200,
        }}
        PaperProps={{
          sx: {
            alignSelf: "start",
            ...(isMobile ? { marginTop: theme.spacing(15) } : {}),
          },
        }}>
        <DialogTitle sx={{ m: 0, p: 4 }} id="customized-dialog-title">
          <FormProvider {...form}>
            <Box
              display="flex"
              flexGrow={1}
              flexDirection="row"
              justifyContent="flex-end">
              <CustomIconButton onClick={handleClose}>
                <Close />
              </CustomIconButton>
            </Box>
            <Box mt={3}>
              <FormTextField
                control={control}
                name="query"
                id="search-query"
                autoFocus
                label=""
                hiddenLabel
                placeholder={placeholder}
                endAdornment={
                  <InputAdornment
                    onClick={() => setValue("query", "")}
                    sx={{ cursor: "pointer" }}
                    position="end">
                    <Close />
                  </InputAdornment>
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </FormProvider>
        </DialogTitle>
        <DialogContent sx={{ m: 0, p: 4 }}>
          {loading && <CircularProgress />}

          {!loading && props.type === "CUSTOM_CONTENT" && props.resultsContent}

          {props.type === "DEFAULT_CONTENT" &&
            props.rows &&
            props.rows.length === 0 && (
              <Typography variant="h3" justifyContent="center">
                No results found
              </Typography>
            )}
          {!loading &&
            props.type === "DEFAULT_CONTENT" &&
            props.rows &&
            props.rows.length > 0 && (
              <Box mt={2} mb={2}>
                <Typography variant="h4" pl={1}>
                  {props.resultsTitle ?? ""}
                </Typography>
                <Table
                  id="search-table"
                  rows={props.rows}
                  columns={props.columns}
                  capitalizeRow={false}
                  onRowActionClick={handleClose}
                  onTableRowClick={handleClose}
                />
              </Box>
            )}
        </DialogContent>
      </Dialog>
    </>
  );
};
